import React, { useMemo, useState } from "react";
import {
  Button,
  Group,
  Table,
  Text,
  Title,
  useMantineColorScheme,
  Container,
  Badge,
  Paper,
  Loader,
  Center,
  Avatar,
  Tooltip,
  Pagination,
} from "@mantine/core";
import { IconArrowLeft, IconEye } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useExecutedTransactionsQuery } from "generated/graphql";
import { format } from "date-fns";
import { getTransactionTypeLabel, getTransactionTypeColor } from "./transactionUtils";

const PAGE_SIZE = 20;

/**
 * TransactionHistory
 *
 * Displays executed (completed) transactions for review.
 * Read-only view of historical transaction data.
 */
export default function TransactionHistory() {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const [activePage, setActivePage] = useState(1);

  const { data, loading, error, refetch } = useExecutedTransactionsQuery({
    variables: {
      skip: (activePage - 1) * PAGE_SIZE,
      take: PAGE_SIZE,
    },
    fetchPolicy: "cache-and-network",
  });

  const transactions = useMemo(() => {
    return (data?.transactions?.items ?? []).map((t) => ({
      id: t.id,
      type: t.transactionType,
      dateSubmitted: t.dateSubmitted,
      dateExecuted: t.dateExecuted,
      submitter: t.submitter,
      submittingFranchise: t.submittingFranchise,
      tradePartnerFranchise: t.tradePartnerFranchise,
      notes: t.notes,
    }));
  }, [data]);

  const totalCount = data?.transactions?.totalCount ?? 0;
  const pageCount = Math.ceil(totalCount / PAGE_SIZE);

  function onViewTransaction(id: string) {
    navigate(`/tm-hub/transactions/${id}`);
  }

  return (
    <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
      <Group justify="flex-start" mb="sm">
        <Button
          variant="filled"
          color="blue"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/tm-hub")}
          size="sm"
        >
          Back to Transaction Manager Hub
        </Button>
      </Group>

      <div className="space-y-4">
        <Title c={isDark ? "white" : "black"} size="h3" className="sm:text-2xl">
          Transaction History
        </Title>
        <Text c={isDark ? "gray.4" : "gray.7"} size="sm">
          Completed transactions. Click a row to view details.
        </Text>

        <Paper withBorder radius="md" shadow="sm" className="overflow-x-auto">
          <Table
            striped
            highlightOnHover
            withTableBorder
            withColumnBorders
            horizontalSpacing="md"
            verticalSpacing="sm"
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Type</Table.Th>
                <Table.Th>Submitted By</Table.Th>
                <Table.Th>Franchise</Table.Th>
                <Table.Th>Submitted</Table.Th>
                <Table.Th>Executed</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Center py="md">
                      <Loader size="sm" />
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : error ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text size="sm" c="red">
                      Failed to load transaction history. {error.message}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : transactions.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>
                      No executed transactions found.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                transactions.map((t) => (
                  <Table.Tr
                    key={t.id}
                    onClick={() => onViewTransaction(t.id)}
                    role="button"
                    tabIndex={0}
                    aria-label={`View ${getTransactionTypeLabel(t.type)} transaction executed on ${t.dateExecuted ? format(new Date(t.dateExecuted), "MMM d, yyyy") : "unknown date"}`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        onViewTransaction(t.id);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <Table.Td>
                      <Badge color={getTransactionTypeColor(t.type)} variant="light">
                        {getTransactionTypeLabel(t.type)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Avatar src={t.submitter?.avatarUrl} size="sm" radius="xl" />
                        <Text size="sm">{t.submitter?.name || "Unknown"}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Text size="sm">
                          {t.submittingFranchise?.prefix
                            ? `[${t.submittingFranchise.prefix}] `
                            : ""}
                          {t.submittingFranchise?.name || "—"}
                        </Text>
                        {t.tradePartnerFranchise && (
                          <>
                            <Text size="xs" c="dimmed">
                              with
                            </Text>
                            <Text size="sm">
                              {t.tradePartnerFranchise.prefix
                                ? `[${t.tradePartnerFranchise.prefix}]`
                                : t.tradePartnerFranchise.name}
                            </Text>
                          </>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label={new Date(t.dateSubmitted).toLocaleString()}>
                        <Text size="sm">
                          {format(new Date(t.dateSubmitted), "MMM d, yyyy")}
                        </Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>
                      {t.dateExecuted ? (
                        <Tooltip label={new Date(t.dateExecuted).toLocaleString()}>
                          <Badge color="green" variant="light">
                            {format(new Date(t.dateExecuted), "MMM d, yyyy")}
                          </Badge>
                        </Tooltip>
                      ) : (
                        <Text size="sm" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Tooltip label="View Details">
                          <Button
                            size="xs"
                            variant="light"
                            color="blue"
                            onClick={(e) => {
                              e.stopPropagation();
                              onViewTransaction(t.id);
                            }}
                            aria-label="View transaction details"
                          >
                            <IconEye size={14} />
                          </Button>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Paper>

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <Text size="sm" c={isDark ? "gray.5" : "gray.6"} className="text-center sm:text-left">
            Showing {transactions.length} of {totalCount} completed transaction
            {totalCount === 1 ? "" : "s"}
          </Text>
          {pageCount > 1 && (
            <Pagination
              total={pageCount}
              value={activePage}
              onChange={(page) => {
                setActivePage(page);
                refetch({
                  skip: (page - 1) * PAGE_SIZE,
                  take: PAGE_SIZE,
                });
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              size="sm"
              className="justify-center sm:justify-end"
            />
          )}
        </div>
      </div>
    </Container>
  );
}
